import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const EquipmentPage = () => {
  const [equipment, setEquipment] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [filter, setFilter] = useState({ equipment_type: '', status: '' });
  const [alerts, setAlerts] = useState([]);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    equipment_type: 'laptop',
    brand: '',
    model: '',
    serial_number: '',
    asset_tag: '',
    purchase_date: '',
    purchase_price: '',
    warranty_expiry: '',
    bitlocker_key: '',
    mdm_id: '',
    mdm_enrolled: false,
    specs: { ram: '', storage: '', processor: '' },
    has_company_number: false,
    company_phone_number: '',
    notes: ''
  });
  const [assignData, setAssignData] = useState({ employee_id: '', notes: '' });
  const [licenseData, setLicenseData] = useState({
    software_name: '',
    license_key: '',
    license_type: 'perpetual',
    expiry_date: '',
    cost: ''
  });
  const [tenants, setTenants] = useState([]);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchEquipment();
    fetchEmployees();
    fetchAlerts();
    if (isSuperAdmin) fetchTenants();
  }, [filter]);

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await api.get('/hr/alerts');
      const equipmentAlerts = response.data.alerts.filter(a => a.entity_type === 'equipment');
      setAlerts(equipmentAlerts);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  const fetchEquipment = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.equipment_type) params.append('equipment_type', filter.equipment_type);
      if (filter.status) params.append('status', filter.status);
      
      const response = await api.get(`/hr/equipment?${params.toString()}`);
      setEquipment(response.data);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      toast.error('Erro ao carregar equipamentos');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees?limit=500');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/equipment', formData);
      toast.success('Equipamento criado com sucesso');
      setShowModal(false);
      resetForm();
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar equipamento');
    }
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hr/equipment/${selectedEquipment.equipment_id}/assign?employee_id=${assignData.employee_id}&notes=${encodeURIComponent(assignData.notes || '')}`);
      toast.success('Equipamento atribuído com sucesso');
      setShowAssignModal(false);
      setSelectedEquipment(null);
      setAssignData({ employee_id: '', notes: '' });
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atribuir equipamento');
    }
  };

  const handleReturn = async (equipmentId) => {
    try {
      await api.post(`/hr/equipment/${equipmentId}/return?condition=good`);
      toast.success('Equipamento devolvido');
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao devolver equipamento');
    }
  };

  const handleDelete = async (equipmentId) => {
    if (!window.confirm('Tem certeza que deseja eliminar este equipamento?')) return;
    try {
      await api.delete(`/hr/equipment/${equipmentId}`);
      toast.success('Equipamento eliminado');
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar equipamento');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImportLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/hr/equipment/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${response.data.imported} equipamentos importados com sucesso`);
      if (response.data.errors > 0) {
        toast.warning(`${response.data.errors} linhas com erros`);
      }
      setShowImportModal(false);
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao importar ficheiro');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAddLicense = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/hr/equipment/${selectedEquipment.equipment_id}/license`, licenseData);
      toast.success('Licença adicionada');
      setShowLicenseModal(false);
      setLicenseData({ software_name: '', license_key: '', license_type: 'perpetual', expiry_date: '', cost: '' });
      fetchEquipment();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar licença');
    }
  };

  const resetForm = () => {
    setFormData({
      equipment_type: 'laptop',
      brand: '',
      model: '',
      serial_number: '',
      asset_tag: '',
      purchase_date: '',
      purchase_price: '',
      warranty_expiry: '',
      bitlocker_key: '',
      mdm_id: '',
      mdm_enrolled: false,
      specs: { ram: '', storage: '', processor: '' },
      has_company_number: false,
      company_phone_number: '',
      notes: ''
    });
  };

  const getTypeBadge = (type) => {
    const icons = {
      laptop: '💻',
      desktop: '🖥️',
      tablet: '📱',
      phone: '📞',
      monitor: '🖵',
      other: '📦'
    };
    const labels = {
      laptop: 'Laptop',
      desktop: 'Desktop',
      tablet: 'Tablet',
      phone: 'Telemóvel',
      monitor: 'Monitor',
      other: 'Outro'
    };
    return (
      <span className="flex items-center gap-1">
        <span>{icons[type] || '📦'}</span>
        <span>{labels[type] || type}</span>
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const colors = {
      available: 'bg-emerald-100 text-emerald-700',
      assigned: 'bg-blue-100 text-blue-700',
      maintenance: 'bg-amber-100 text-amber-700',
      retired: 'bg-gray-100 text-gray-700',
      lost: 'bg-red-100 text-red-700'
    };
    const labels = {
      available: 'Disponível',
      assigned: 'Atribuído',
      maintenance: 'Manutenção',
      retired: 'Abatido',
      lost: 'Perdido'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.employee_id === employeeId);
    return emp?.name || employeeId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="equipment-page" className="space-y-6 animate-fade-in">
      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="rounded-xl border border-amber-500/50 bg-amber-50 dark:bg-amber-900/20 p-4">
          <div className="flex items-center gap-2 mb-2">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="font-semibold text-amber-700 dark:text-amber-400">Alertas de Garantia ({alerts.length})</span>
          </div>
          <div className="space-y-2">
            {alerts.slice(0, 3).map((alert, idx) => (
              <div key={idx} className={`text-sm p-2 rounded ${alert.priority === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-700' : alert.priority === 'high' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700'}`}>
                {alert.message}
              </div>
            ))}
            {alerts.length > 3 && <p className="text-xs text-amber-600">+{alerts.length - 3} mais alertas</p>}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Equipamentos IT</h1>
          <p className="text-muted-foreground">Gestão de laptops, tablets, telemóveis e outros dispositivos</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 rounded-lg border border-input bg-background hover:bg-muted transition-colors flex items-center gap-2"
            data-testid="import-equipment-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Importar
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Equipamento
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {['available', 'assigned', 'maintenance', 'retired', 'lost'].map(status => (
          <div key={status} className="rounded-xl border bg-card p-4">
            <p className="text-2xl font-bold">{equipment.filter(e => e.status === status).length}</p>
            <p className="text-xs text-muted-foreground">{getStatusBadge(status)}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Tipo</label>
            <select
              value={filter.equipment_type}
              onChange={(e) => setFilter({ ...filter, equipment_type: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
            >
              <option value="">Todos</option>
              <option value="laptop">Laptop</option>
              <option value="desktop">Desktop</option>
              <option value="tablet">Tablet</option>
              <option value="phone">Telemóvel</option>
              <option value="monitor">Monitor</option>
              <option value="other">Outro</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Estado</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
            >
              <option value="">Todos</option>
              <option value="available">Disponível</option>
              <option value="assigned">Atribuído</option>
              <option value="maintenance">Manutenção</option>
              <option value="retired">Abatido</option>
            </select>
          </div>
        </div>
      </div>

      {/* Equipment Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {equipment.length === 0 ? (
          <div className="col-span-full rounded-xl border bg-card p-8 text-center">
            <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p className="text-muted-foreground">Sem equipamentos registados</p>
          </div>
        ) : (
          equipment.map((item) => (
            <div key={item.equipment_id} className="rounded-xl border bg-card p-5 hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div>
                  {getTypeBadge(item.equipment_type)}
                  <h3 className="font-semibold mt-1">{item.brand} {item.model}</h3>
                </div>
                {getStatusBadge(item.status)}
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nº Série:</span>
                  <span className="font-mono">{item.serial_number || '-'}</span>
                </div>
                {item.asset_tag && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Asset Tag:</span>
                    <span>{item.asset_tag}</span>
                  </div>
                )}
                {item.assigned_to && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Atribuído a:</span>
                    <span className="font-medium">{getEmployeeName(item.assigned_to)}</span>
                  </div>
                )}
                {item.equipment_type === 'phone' && item.has_company_number && item.company_phone_number && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Nº Empresa:</span>
                    <span className="font-medium text-primary">{item.company_phone_number}</span>
                  </div>
                )}
                {item.warranty_expiry && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Garantia:</span>
                    <span className={new Date(item.warranty_expiry) < new Date() ? 'text-red-600' : ''}>{item.warranty_expiry}</span>
                  </div>
                )}
                {item.mdm_id && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">MDM/Intune:</span>
                    <span className="font-mono text-xs">{item.mdm_id}</span>
                  </div>
                )}
                {item.software_licenses?.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Licenças:</span>
                    <span>{item.software_licenses.length}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-2">
                {item.status === 'available' && (
                  <button
                    onClick={() => { setSelectedEquipment(item); setShowAssignModal(true); }}
                    className="px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm font-medium transition-colors"
                  >
                    Atribuir
                  </button>
                )}
                {item.status === 'assigned' && (
                  <button
                    onClick={() => handleReturn(item.equipment_id)}
                    className="px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 text-sm font-medium transition-colors"
                  >
                    Devolver
                  </button>
                )}
                <button
                  onClick={() => { setSelectedEquipment(item); setShowLicenseModal(true); }}
                  className="px-3 py-1.5 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 text-sm font-medium transition-colors"
                >
                  + Licença
                </button>
                <button
                  onClick={() => handleDelete(item.equipment_id)}
                  className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium transition-colors"
                  data-testid={`delete-equipment-${item.equipment_id}`}
                >
                  Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Equipment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Novo Equipamento IT</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {/* Tenant selection for Super Admin */}
              {isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select
                    value={formData.tenant_id || ''}
                    onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  >
                    <option value="">Selecionar Tenant</option>
                    {tenants.map(t => (
                      <option key={t.tenant_id} value={t.tenant_id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo</label>
                  <select
                    value={formData.equipment_type}
                    onChange={(e) => setFormData({ ...formData, equipment_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  >
                    <option value="laptop">Laptop</option>
                    <option value="desktop">Desktop</option>
                    <option value="tablet">Tablet</option>
                    <option value="phone">Telemóvel</option>
                    <option value="monitor">Monitor</option>
                    <option value="other">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Marca</label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Modelo</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nº Série</label>
                  <input
                    type="text"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Asset Tag</label>
                  <input
                    type="text"
                    value={formData.asset_tag}
                    onChange={(e) => setFormData({ ...formData, asset_tag: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data Aquisição</label>
                  <input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Preço (€)</label>
                  <input
                    type="number"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fim Garantia</label>
                  <input
                    type="date"
                    value={formData.warranty_expiry}
                    onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <h3 className="font-medium mb-3">Segurança & MDM</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Chave BitLocker</label>
                    <input
                      type="text"
                      value={formData.bitlocker_key}
                      onChange={(e) => setFormData({ ...formData, bitlocker_key: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background font-mono text-sm"
                      placeholder="XXXXXX-XXXXXX-..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ID MDM/Intune</label>
                    <input
                      type="text"
                      value={formData.mdm_id}
                      onChange={(e) => setFormData({ ...formData, mdm_id: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background font-mono text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Company Phone Number - Only for phones */}
              {formData.equipment_type === 'phone' && (
                <div className="border-t border-border pt-4">
                  <h3 className="font-medium mb-3">Número de Telefone da Empresa</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="has_company_number"
                        checked={formData.has_company_number}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          has_company_number: e.target.checked,
                          company_phone_number: e.target.checked ? formData.company_phone_number : ''
                        })}
                        className="rounded border-input"
                      />
                      <label htmlFor="has_company_number" className="text-sm">
                        Tem número da empresa atribuído?
                      </label>
                    </div>
                    {formData.has_company_number && (
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Número de Telefone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          value={formData.company_phone_number}
                          onChange={(e) => setFormData({ ...formData, company_phone_number: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                          placeholder="+351 9XX XXX XXX"
                          required={formData.has_company_number}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Número corporativo atribuído a este telemóvel
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="border-t border-border pt-4">
                <h3 className="font-medium mb-3">Especificações</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">RAM</label>
                    <input
                      type="text"
                      value={formData.specs.ram}
                      onChange={(e) => setFormData({ ...formData, specs: { ...formData.specs, ram: e.target.value } })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                      placeholder="16GB"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Armazenamento</label>
                    <input
                      type="text"
                      value={formData.specs.storage}
                      onChange={(e) => setFormData({ ...formData, specs: { ...formData.specs, storage: e.target.value } })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                      placeholder="512GB SSD"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Processador</label>
                    <input
                      type="text"
                      value={formData.specs.processor}
                      onChange={(e) => setFormData({ ...formData, specs: { ...formData.specs, processor: e.target.value } })}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                      placeholder="Intel i7"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notas</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Criar Equipamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedEquipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Atribuir Equipamento</h2>
              <p className="text-sm text-muted-foreground">{selectedEquipment.brand} {selectedEquipment.model}</p>
            </div>
            <form onSubmit={handleAssign} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Colaborador</label>
                <select
                  value={assignData.employee_id}
                  onChange={(e) => setAssignData({ ...assignData, employee_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  required
                >
                  <option value="">Selecionar colaborador</option>
                  {employees.map(emp => (
                    <option key={emp.employee_id} value={emp.employee_id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notas</label>
                <textarea
                  value={assignData.notes}
                  onChange={(e) => setAssignData({ ...assignData, notes: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowAssignModal(false); setSelectedEquipment(null); }}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Atribuir
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* License Modal */}
      {showLicenseModal && selectedEquipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Adicionar Licença de Software</h2>
              <p className="text-sm text-muted-foreground">{selectedEquipment.brand} {selectedEquipment.model}</p>
            </div>
            <form onSubmit={handleAddLicense} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Software</label>
                <input
                  type="text"
                  value={licenseData.software_name}
                  onChange={(e) => setLicenseData({ ...licenseData, software_name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  placeholder="Microsoft Office 365"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Chave de Licença</label>
                <input
                  type="text"
                  value={licenseData.license_key}
                  onChange={(e) => setLicenseData({ ...licenseData, license_key: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background font-mono"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo</label>
                  <select
                    value={licenseData.license_type}
                    onChange={(e) => setLicenseData({ ...licenseData, license_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="perpetual">Perpétua</option>
                    <option value="subscription">Subscrição</option>
                    <option value="volume">Volume</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Expiração</label>
                  <input
                    type="date"
                    value={licenseData.expiry_date}
                    onChange={(e) => setLicenseData({ ...licenseData, expiry_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowLicenseModal(false); setSelectedEquipment(null); }}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Adicionar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-lg">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Importar Equipamentos</h2>
              <p className="text-sm text-muted-foreground">Importe equipamentos a partir de um ficheiro CSV ou Excel</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="rounded-lg border-2 border-dashed border-border p-8 text-center">
                <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-muted-foreground mb-4">Arraste um ficheiro ou clique para selecionar</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleImport}
                  className="hidden"
                  id="import-file"
                  data-testid="import-file-input"
                />
                <label
                  htmlFor="import-file"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer"
                >
                  {importLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      A importar...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Selecionar Ficheiro
                    </>
                  )}
                </label>
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4 text-sm">
                <h4 className="font-medium mb-2">Formato esperado:</h4>
                <p className="text-muted-foreground mb-2">O ficheiro deve conter as seguintes colunas:</p>
                <code className="block text-xs bg-background p-2 rounded">
                  tipo;marca;modelo;numero_serie;asset;data_compra;preco;garantia;bitlocker;mdm;notas
                </code>
                <p className="text-xs text-muted-foreground mt-2">
                  Tipos válidos: laptop, desktop, tablet, phone, monitor, other
                </p>
              </div>
              
              <div className="flex justify-end">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EquipmentPage;
